import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { http, currency, STATUS_CONFIG } from "../lib/api";
import { Button } from "../components/ui/button";
import { Loader2, RefreshCw, X, Clock, Check, AlertTriangle, Flame } from "lucide-react";
import { toast } from "sonner";

// Priority palette shown only on the production board (internal).
const PRIORITY_CFG = {
  urgente: { label: "Urgente", color: "#DC2626", ring: "ring-red-500" },
  medio:   { label: "Medio",   color: "#F59E0B", ring: "ring-amber-500" },
  esperar: { label: "Esperar", color: "#3B82F6", ring: "ring-blue-500" },
};

// Find when the order entered "en_produccion" using its history log.
// Returns ISO string or null.
function findProductionSince(order) {
  const hist = Array.isArray(order.history) ? order.history : [];
  for (let i = hist.length - 1; i >= 0; i--) {
    if (hist[i].status === "en_produccion") return hist[i].at;
  }
  return order.updated_at || order.created_at || null;
}

function daysSince(iso) {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return 0;
  return (Date.now() - t) / 86400000;
}

// Full-screen production board. Only shows orders in "en_produccion".
// Each card has an internal checkbox "Producción terminada". Marking it stores
// production_done + production_done_at on the order. NO client notification, NO
// status change — purely internal control.
export default function ProductionBoard() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());
  const [updating, setUpdating] = useState({}); // { orderId: bool }

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/admin/orders", { params: { status: "en_produccion" } });
      // Only pending production — once marked done, order disappears from the board
      // and is only visible in the admin panel via its checkbox.
      setOrders((data || []).filter((o) => !o.production_done));
    } catch {
      toast.error("No se pudieron cargar los pedidos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  const { pending } = useMemo(() => {
    // Only pending items — done ones are stripped in load()
    return { pending: orders };
  }, [orders]);

  const toggleDone = async (order) => {
    setUpdating((u) => ({ ...u, [order.order_id]: true }));
    try {
      await http.put(`/admin/orders/${order.order_id}/production-done`, { done: true });
      // Remove from the board immediately — visible now only in the admin panel checkbox.
      setOrders((prev) => prev.filter((o) => o.order_id !== order.order_id));
      toast.success(`✅ Pedido ${order.order_id.slice(0, 10)} terminado — visible en el panel de administración`, { duration: 4000 });
    } catch {
      toast.error("No se pudo actualizar el estado interno");
    } finally {
      setUpdating((u) => ({ ...u, [order.order_id]: false }));
    }
  };

  const setPriority = async (order, priority) => {
    // priority can be "urgente" | "medio" | "esperar" | null (to clear)
    setUpdating((u) => ({ ...u, [order.order_id]: true }));
    try {
      const { data } = await http.put(`/admin/orders/${order.order_id}/production-priority`, { priority });
      setOrders((prev) => prev.map((o) => (o.order_id === order.order_id ? data : o)));
      toast.success(priority ? `Prioridad: ${PRIORITY_CFG[priority].label}` : "Prioridad quitada");
    } catch {
      toast.error("No se pudo cambiar la prioridad");
    } finally {
      setUpdating((u) => ({ ...u, [order.order_id]: false }));
    }
  };

  // Sort: urgente > medio > (no priority) > esperar; within same group, oldest-in-production first
  const priorityRank = { urgente: 0, medio: 1, esperar: 3 };
  const sortedPending = useMemo(() => {
    return [...(pending || [])].sort((a, b) => {
      const ra = priorityRank[a.production_priority] ?? 2;
      const rb = priorityRank[b.production_priority] ?? 2;
      if (ra !== rb) return ra - rb;
      // Older in production first (more urgent)
      const ta = new Date(findProductionSince(a) || 0).getTime();
      const tb = new Date(findProductionSince(b) || 0).getTime();
      return ta - tb;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  const timeStr = now.toLocaleTimeString("es-MX", {
    timeZone: "America/Monterrey",
    hour: "2-digit",
    minute: "2-digit",
  });
  const dateStr = now.toLocaleDateString("es-MX", {
    timeZone: "America/Monterrey",
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  const prodColor = STATUS_CONFIG.en_produccion.color;

  return (
    <div className="min-h-screen bg-neutral-950 text-white flex flex-col" data-testid="production-board">
      {/* Header — sticky at top so it's always visible while scrolling */}
      <div className="sticky top-0 z-30 flex flex-wrap items-center justify-between gap-3 px-3 sm:px-6 py-3 sm:py-4 border-b-2 border-neutral-800 shrink-0 bg-neutral-950/95 backdrop-blur">
        <div className="min-w-0">
          <div className="text-[10px] sm:text-[11px] font-mono uppercase text-neutral-500 tracking-widest">Producción · Tablero en vivo</div>
          <div className="font-display text-lg sm:text-2xl leading-tight">Pedidos en producción</div>
        </div>
        <div className="flex items-center gap-2 sm:gap-4 md:gap-6 flex-wrap justify-end">
          <div className="flex items-center gap-3 sm:gap-6 sm:pr-4 sm:border-r sm:border-neutral-800">
            <div className="text-center">
              <div className="text-[9px] sm:text-[10px] font-mono uppercase text-neutral-500">En producción</div>
              <div className="font-display text-2xl sm:text-3xl leading-none" style={{ color: prodColor }} data-testid="board-count-pending">{sortedPending.length}</div>
            </div>
          </div>
          <div className="text-right hidden sm:block">
            <div className="flex items-center gap-2 justify-end text-2xl sm:text-3xl font-display tracking-tight" data-testid="board-clock">
              <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400" />
              {timeStr}
            </div>
            <div className="text-[10px] sm:text-[11px] font-mono uppercase text-neutral-500">{dateStr} · Monterrey</div>
          </div>
          {/* Mobile-only compact clock */}
          <div className="sm:hidden flex items-center gap-1 text-lg font-display tracking-tight" data-testid="board-clock-mobile">
            <Clock className="w-4 h-4 text-emerald-400" />
            {timeStr}
          </div>
          <button
            onClick={load}
            className="p-2 sm:p-3 hover:bg-neutral-800 border border-neutral-700 transition-colors"
            title="Actualizar"
            data-testid="board-refresh"
          >
            <RefreshCw className={`w-4 h-4 sm:w-5 sm:h-5 ${loading ? "animate-spin" : ""}`} />
          </button>
          <Link
            to="/admin"
            className="p-2 sm:p-3 hover:bg-neutral-800 border border-neutral-700 transition-colors"
            title="Salir del tablero"
            data-testid="board-exit"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </Link>
        </div>
      </div>

      {/* Body: natural page scroll so all orders are reachable on any device */}
      <div className="flex-1 p-2 sm:p-3">
        <Section
          title="En producción"
          color={prodColor}
          items={sortedPending}
          emptyText="Sin pedidos en producción. Cuando un pedido pase a producción aparecerá aquí."
          testId="board-pending"
        >
          {(o) => (
            <ProductionCard
              key={o.order_id}
              order={o}
              accent={prodColor}
              updating={!!updating[o.order_id]}
              onDone={() => toggleDone(o)}
              onSetPriority={(p) => setPriority(o, p)}
            />
          )}
        </Section>
      </div>

      {loading && orders.length === 0 && (
        <div className="fixed inset-0 flex items-center justify-center bg-neutral-950/80 z-40">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
        </div>
      )}
    </div>
  );
}

function Section({ title, color, items, emptyText, testId, children }) {
  return (
    <div className="flex flex-col bg-neutral-900 border-t-4" style={{ borderTopColor: color }} data-testid={testId}>
      <div className="px-4 py-2 flex items-center justify-between shrink-0 sticky top-[68px] sm:top-[80px] z-20 bg-neutral-900/95 backdrop-blur">
        <div className="text-[11px] font-mono uppercase tracking-widest" style={{ color }}>
          {title}
        </div>
        <div className="font-display text-2xl leading-none" data-testid={`${testId}-count`}>{items.length}</div>
      </div>
      <div className="px-2 sm:px-3 pb-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2 sm:gap-3 auto-rows-min" data-testid={`${testId}-list`}>
        {items.length === 0 ? (
          <div className="col-span-full text-neutral-600 text-xs italic text-center py-6">{emptyText}</div>
        ) : (
          items.map(children)
        )}
      </div>
    </div>
  );
}

function ProductionCard({ order, accent, updating, onDone, onSetPriority }) {
  const clientName = order.user_name || order.shipping?.name || "—";
  const phone = order.shipping?.phone || "";
  const project = order.project || "";
  const items = order.items || [];
  const custom = order.custom_items || [];
  const totalUnits =
    items.reduce((a, it) => a + Number(it.quantity || 0), 0) +
    custom.reduce((a, it) => a + Number(it.quantity || 0), 0);

  const previews = [];
  for (const it of items) {
    const variant = [it.color_label, it.size_label, it.printing_method_label].filter(Boolean).join(" · ");
    previews.push({ name: it.product_name, variant, qty: it.quantity, isCustom: false });
  }
  for (const ci of custom) {
    previews.push({ name: ci.name, variant: ci.description || "", qty: ci.quantity, isCustom: true });
  }
  const visible = previews.slice(0, 4);
  const remaining = previews.length - visible.length;

  // Priority + urgency (2+ days in production without change)
  const priority = order.production_priority || null;
  const priCfg = priority ? PRIORITY_CFG[priority] : null;
  const productionSince = findProductionSince(order);
  const daysInProduction = daysSince(productionSince);
  const isOverdue = daysInProduction >= 2; // ≥ 2 days sin cambio ⇒ rojo
  const sinceStr = productionSince
    ? new Date(productionSince).toLocaleDateString("es-MX", {
        timeZone: "America/Monterrey", day: "2-digit", month: "2-digit", year: "2-digit",
      })
    : null;

  // Border color: urgencia rojo > prioridad > accent normal
  const borderColor = isOverdue ? "#DC2626" : (priCfg?.color || accent);

  return (
    <div
      className={`border-l-4 p-3 space-y-2 bg-neutral-800 transition-colors relative ${isOverdue ? "ring-2 ring-red-500/60 bg-red-950/30" : ""}`}
      style={{ borderLeftColor: borderColor }}
      data-testid={`board-card-${order.order_id}`}
    >
      {isOverdue && (
        <div
          className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] font-mono uppercase px-2 py-0.5 flex items-center gap-1 shadow-lg"
          title={`Lleva ${Math.floor(daysInProduction)} día(s) en producción sin cambio`}
          data-testid={`board-card-overdue-${order.order_id}`}
        >
          <Flame className="w-3 h-3" /> {Math.floor(daysInProduction)}d sin cambio
        </div>
      )}

      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[10px] text-neutral-500 truncate">
            {order.order_number ? `#${order.order_number} · ${order.order_id}` : order.order_id}
          </div>
          <div className="font-display text-lg leading-tight truncate" data-testid={`board-card-client-${order.order_id}`}>
            {clientName}
          </div>
          {phone && (
            <div className="text-[10px] font-mono text-neutral-500">📱 {phone}</div>
          )}
          {sinceStr && (
            <div className={`text-[10px] font-mono mt-0.5 flex items-center gap-1 ${isOverdue ? "text-red-400" : "text-neutral-500"}`}>
              {isOverdue && <AlertTriangle className="w-3 h-3" />}
              En producción desde {sinceStr}
            </div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] font-mono text-neutral-500 mt-0.5">{totalUnits} pz</div>
        </div>
      </div>

      {/* Priority selector — internal to production operator */}
      <div className="flex items-center gap-1" data-testid={`board-card-priority-${order.order_id}`}>
        <span className="text-[9px] font-mono uppercase text-neutral-500 shrink-0 pr-1">Prioridad</span>
        {["urgente", "medio", "esperar"].map((k) => {
          const cfg = PRIORITY_CFG[k];
          const active = priority === k;
          return (
            <button
              key={k}
              onClick={() => onSetPriority(active ? null : k)}
              disabled={updating}
              className={`flex-1 h-7 text-[10px] font-mono uppercase border transition-colors ${
                active
                  ? "text-white border-transparent"
                  : "text-neutral-300 bg-neutral-900 border-neutral-700 hover:border-neutral-500"
              }`}
              style={active ? { backgroundColor: cfg.color } : {}}
              data-testid={`board-card-priority-${k}-${order.order_id}`}
              title={active ? "Quitar prioridad" : `Marcar como ${cfg.label}`}
            >
              {cfg.label}
            </button>
          );
        })}
      </div>

      {project && (
        <div
          className="text-xs font-mono leading-snug whitespace-pre-wrap border-l-4 pl-2 py-1.5 pr-2 bg-neutral-900/70 text-emerald-300 break-words"
          style={{ borderColor: borderColor }}
          data-testid={`board-card-project-${order.order_id}`}
        >
          {project}
        </div>
      )}

      <ul className="space-y-1" data-testid={`board-card-items-${order.order_id}`}>
        {visible.map((v, i) => (
          <li key={i} className="flex items-start gap-2 text-[12px] leading-snug">
            <span
              className={`inline-block px-1.5 py-0.5 text-[10px] font-mono ${
                v.isCustom ? "bg-amber-500/20 text-amber-300" : "bg-neutral-700 text-neutral-200"
              }`}
            >
              x{v.qty}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-neutral-100 truncate">{v.name}</div>
              {v.variant && <div className="text-neutral-400 text-[10px] truncate">{v.variant}</div>}
            </div>
          </li>
        ))}
        {remaining > 0 && (
          <li className="text-[10px] text-neutral-500 italic">+{remaining} más</li>
        )}
      </ul>

      <div className="pt-1">
        <Button
          size="sm"
          className="w-full rounded-none h-11 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
          onClick={onDone}
          disabled={updating}
          data-testid={`board-card-done-${order.order_id}`}
        >
          {updating ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Check className="w-4 h-4 mr-2" /> Marcar como terminado
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
